'use client'

import { useEffect, useRef, useState } from 'react'

import RichText from '@/components/RichText'
import { cn } from '@/utilities/ui'

import type { TextBlock as TextBlockProps } from '@/payload-types'

export const TextBlock: React.FC<TextBlockProps> = ({ textSections, showMenu }) => {
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const menuLinksRef = useRef<HTMLDivElement | null>(null)
  const [menuHeight, setMenuHeight] = useState(0)

  useEffect(() => {
    const el = menuLinksRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setMenuHeight(entry.contentRect.height)
      }
    })
    observer.observe(el)
    setMenuHeight(el.getBoundingClientRect().height)
    return () => observer.disconnect()
  }, [showMenu, textSections])

  useEffect(() => {
    // Add hanging indent to paragraphs that start with bullet points
    const addBulletIndent = () => {
      const richTextContainers = document.querySelectorAll('.payload-richtext')
      richTextContainers.forEach((container) => {
        const paragraphs = container.querySelectorAll('p')
        paragraphs.forEach((p) => {
          const text = p.textContent || ''
          if (text.trim().startsWith('•')) {
            p.classList.add('bullet-indent')
          }
        })
      })
    }

    const timeoutId = setTimeout(addBulletIndent, 100)
    return () => clearTimeout(timeoutId)
  }, [textSections])

  return (
    <>
      <div className="wrapper flex flex-row gap-10 px-10 min-[700px]:px-24">
        <div
          className={cn(
            'flex w-full flex-col gap-10',
            showMenu ? 'min-[1200px]:w-3/4 min-[1200px]:pr-24' : '',
          )}
        >
          {textSections?.map((section: NonNullable<TextBlockProps['textSections']>[number]) => {
            const isCitations =
              section.sectionHeader === 'Citations' || section.sectionHeader === 'Sources'
            return (
              <div
                key={section.sectionHeader}
                id={section.sectionHeader}
                ref={(el) => {
                  sectionRefs.current[section.sectionHeader] = el
                }}
                className={cn(
                  'flex scroll-mt-[25vh] flex-col gap-4',
                  isCitations &&
                    'relative inset-x-1/2 -mx-[50vw] w-screen px-10 min-[700px]:px-24',
                )}
              >
                <h2 className="font-family-body text-xl font-semibold text-foreground min-[700px]:text-2xl">
                  {section.sectionHeader}
                </h2>
                <RichText
                  className={cn(
                    'mx-0 font-family-body wrap-break-word text-neutral-600 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6',
                    isCitations ? 'text-sm' : 'text-base',
                  )}
                  data={section.sectionContent}
                />
              </div>
            )
          })}
        </div>
        {showMenu && (
          <div className="sticky top-16 mr-8 hidden flex-row self-start min-[1200px]:flex min-[1200px]:w-1/4">
            <div
              className="mr-12 w-[1.5px] shrink-0 bg-neutral-200"
              style={{ height: menuHeight ? `${menuHeight}px` : undefined }}
            />
            <div ref={menuLinksRef} className="flex flex-col gap-4">
              {textSections?.map((section: NonNullable<TextBlockProps['textSections']>[number]) => (
                <a
                  key={section.sectionHeader}
                  href={`#${section.sectionHeader}`}
                  className="font-family-body text-base text-neutral-600 hover:font-semibold hover:text-neutral-900"
                  onClick={(e) => {
                    e.preventDefault()
                    sectionRefs.current[section.sectionHeader]?.scrollIntoView({
                      behavior: 'smooth',
                    })
                  }}
                >
                  {section.sectionHeader}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
